import React from 'react';

/**
 * Lane 1342: two sections on the supplier dossier's "Team knowledge" tab.
 *
 *  - "What <supplier> has told you": the supplier's letters and notices, in
 *    the attributed voice the chat uses ("<supplier> stated ..."). A notice is
 *    the supplier's claim, never presented as our measurement.
 *  - "Your decisions for this supplier": arguments ruled out, arguments to lead
 *    with, how hard the team can push, and approved wording.
 *
 * Data comes from GET /api/suppliers/{id}/on-record, which reads the same
 * records the live chat reads. A section with no rows renders nothing.
 * Render-only: the page owns the fetch.
 */

const POSTURE_LABELS = {
  low: 'Limited leverage',
  normal: 'Normal leverage',
  high: 'Strong leverage',
};

export function formatRecordDate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

export function intelligenceLink(document) {
  if (!document || !document.entry_id) return null;
  return `/intelligence?entry_id=${encodeURIComponent(document.entry_id)}&scope=company`;
}

function Said({ item }) {
  if (!item || !item.said) return null;
  const on = formatRecordDate(item.said_on);
  const when = on ? `Said on ${on}` : 'Said';
  return (
    <div className="sd-fval" style={{ marginTop: '4px' }}>
      {item.said_is_verbatim
        ? <>{when}: &ldquo;{item.said}&rdquo;</>
        : <>{when} (summary, not exact words): {item.said}</>}
    </div>
  );
}

function Row({ children, last }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: last ? 'none' : '1px solid var(--hair)' }}>
      {children}
    </div>
  );
}

function NoticesSection({ supplierName, notices }) {
  const items = (notices && notices.items) || [];
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: '20px' }} data-testid="on-record-notices">
      <div className="sd-lbl" style={{ marginBottom: '10px' }}
        title="Letters and notices the supplier sent you. These are the supplier's claims, not Wingman's measurements.">
        What {supplierName || 'this supplier'} has told you
      </div>
      {items.map((item, idx) => {
        const effective = formatRecordDate(item.effective_date);
        const letter = formatRecordDate(item.letter_date);
        const doc = item.document;
        const uploaded = doc ? formatRecordDate(doc.uploaded_at) : null;
        const link = intelligenceLink(doc);
        const meta = [
          letter ? `Letter dated ${letter}` : null,
          effective ? `Effective ${effective}` : null,
          doc && doc.file_name ? doc.file_name : null,
          doc && (doc.uploaded_by || uploaded)
            ? `Uploaded${doc.uploaded_by ? ` by ${doc.uploaded_by}` : ''}${uploaded ? ` on ${uploaded}` : ''}`
            : null,
        ].filter(Boolean);
        return (
          <Row key={item.assertion_id || idx} last={idx === items.length - 1}>
            <div className="sd-fval">{item.summary}</div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              {meta.map((m) => (
                <span key={m} className="sd-muted" style={{ fontSize: '11px' }}>{m}</span>
              ))}
              {link && (
                <a href={link} className="sd-muted" style={{ fontSize: '11px', marginLeft: 'auto' }}>
                  See in Intelligence
                </a>
              )}
            </div>
          </Row>
        );
      })}
      {notices.more_on_record && (
        <div className="sd-muted" style={{ fontSize: '11px', marginTop: '6px' }}>
          Older notices are on the Intelligence page.
        </div>
      )}
    </div>
  );
}

function DecisionsSection({ decisions }) {
  if (!decisions) return null;
  const ruledOut = decisions.ruled_out || [];
  const leadWith = decisions.lead_with || [];
  const leverage = decisions.leverage;
  const offers = decisions.approved_offers || [];
  const rows = [];
  ruledOut.forEach((item) => {
    const until = formatRecordDate(item.expires_on);
    rows.push({
      key: `out-${item.assertion_id}`,
      body: (
        <>
          <div style={{ fontSize: '13.5px', fontWeight: 600 }}>
            Ruled out as a lever: {item.label}
          </div>
          <Said item={item} />
          {until && <div className="sd-muted" style={{ fontSize: '11px', marginTop: '4px' }}>Applies until {until}</div>}
        </>
      ),
    });
  });
  leadWith.forEach((item) => {
    rows.push({
      key: `lead-${item.assertion_id}`,
      body: (
        <>
          <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Lead with: {item.label}</div>
          <Said item={item} />
        </>
      ),
    });
  });
  if (leverage) {
    rows.push({
      key: `lev-${leverage.assertion_id}`,
      body: (
        <>
          <div style={{ fontSize: '13.5px', fontWeight: 600 }}>
            How hard you can push: {POSTURE_LABELS[leverage.posture] || 'Not stated'}
          </div>
          <Said item={leverage} />
        </>
      ),
    });
  }
  offers.forEach((offer) => {
    const on = formatRecordDate(offer.approved_on);
    rows.push({
      key: `offer-${offer.assertion_id}`,
      body: (
        <>
          <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Approved to say to the supplier</div>
          <div className="sd-fval" style={{ marginTop: '4px' }}>&ldquo;{offer.text}&rdquo;</div>
          {on && <div className="sd-muted" style={{ fontSize: '11px', marginTop: '4px' }}>Approved on {on}</div>}
        </>
      ),
    });
  });
  if (rows.length === 0) return null;
  return (
    <div style={{ marginBottom: '20px' }} data-testid="on-record-decisions">
      <div className="sd-lbl" style={{ marginBottom: '10px' }}
        title="How your team chose to negotiate with this supplier. Wingman's chat follows these.">
        Your decisions for this supplier
      </div>
      {rows.map((row, idx) => (
        <Row key={row.key} last={idx === rows.length - 1}>{row.body}</Row>
      ))}
    </div>
  );
}

export default function SupplierOnRecord({ supplierName, data, failed }) {
  if (failed) {
    return (
      <div className="sd-muted" style={{ fontSize: '12px', marginBottom: '16px' }}>
        Could not load this supplier&apos;s notices and decisions.
      </div>
    );
  }
  if (!data) return null;
  return (
    <>
      <NoticesSection supplierName={data.supplier_name || supplierName} notices={data.notices} />
      <DecisionsSection decisions={data.decisions} />
    </>
  );
}
